import { ClientError, GraphQLClient } from 'graphql-request';
import { PortalOperationError } from './errors';
import { getSdk } from './graphql/generated';

type SDK = ReturnType<typeof getSdk>;
type Operation = keyof SDK;
type OperationVariables = {
  [T in Operation]: Parameters<SDK[T]>[0];
};
type OperationData = {
  [T in Operation]: Awaited<ReturnType<SDK[T]>>;
};

export type GQLRequestArgs<T extends Operation> = {
  server: string;
  apiKey: string;
  timeout?: number;
  operation: T;
  variables: OperationVariables[T];
};

const DEFAULT_TIMEOUT = 5;

export async function gqlRequest<T extends Operation>(
  args: GQLRequestArgs<T>,
): Promise<OperationData[T]> {
  const {
    server: url,
    apiKey,
    operation,
    variables,
    timeout = DEFAULT_TIMEOUT,
  } = args;

  if (!Number.isInteger(timeout) || timeout <= 0) {
    throw new Error(
      `Invalid timeout argument ${timeout}, expected a positive integer`,
    );
  }

  const abortController = new AbortController();

  const client = new GraphQLClient(url, { signal: abortController.signal });
  const sdk = getSdk(client);

  const timer = setTimeout(
    () => {
      abortController.abort();
    },
    timeout * 60 * 1000,
  );

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (await sdk[operation](variables as any, {
      Authorization: `Bearer ${apiKey}`,
    })) as OperationData[T];
  } catch (error) {
    if (error instanceof ClientError) {
      throw new PortalOperationError(error, { operation, url });
    }
    if (abortController.signal.aborted) {
      throw new Error(
        `GraphQL error for operation "${operation}": Request timed out after ${timeout} minutes`,
      );
    }
    throw error;
  } finally {
    clearTimeout(timer);
  }
}
